import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sparkles, ChevronRight, ChevronDown, Info } from 'lucide-react';
import { GENDER_LABELS, AGE_CATEGORY_LABELS } from '@/types/dressCode.types';
import type { DressCodeTemplateGroup } from '@/types/dressCode.types';

interface Props {
  suggestions: DressCodeTemplateGroup[];
  loading: boolean;
  onApply: (templateName: string) => void;
}

const TemplateSuggestions: React.FC<Props> = ({ suggestions, loading, onApply }) => {
  const { t } = useTranslation('events');
  const [expandedTemplate, setExpandedTemplate] = React.useState<string | null>(null);

  if (loading) {
    return (
      <div className="text-sm text-gray-500 animate-pulse">{t('dressCode.suggestions.loading')}</div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-500">
          {t('dressCode.suggestions.empty')}
        </p>
        <p className="text-xs text-gray-400">
          {t('dressCode.suggestions.tip')}
        </p>
      </div>
    );
  }

  const hasAnyMatches = suggestions.some(g => g.match_percentage > 0);

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2 mb-3 p-2 bg-blue-50 rounded-md border border-blue-100">
        <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
        <div className="text-xs text-blue-700">
          {hasAnyMatches ? (
            <>
              <p className="font-medium">{t('dressCode.suggestions.rankedTitle')}</p>
              <p className="mt-0.5 text-blue-600">
                {t('dressCode.suggestions.rankedHelp')}
              </p>
            </>
          ) : (
            <>
              <p className="font-medium">{t('dressCode.suggestions.noTagsTitle')}</p>
              <p className="mt-0.5 text-blue-600">
                {t('dressCode.suggestions.noTagsHelp')}
              </p>
            </>
          )}
        </div>
      </div>

      {suggestions.map(group => {
        const isExpanded = expandedTemplate === group.name;
        return (
          <div
            key={group.name}
            className="rounded-lg border border-gray-200 hover:border-indigo-300 bg-white transition-colors"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Sparkles className="w-4 h-4 text-indigo-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800 truncate">{group.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {group.total_template_tags > 0 ? (
                      <Badge
                        variant={group.match_percentage >= 80 ? 'default' : group.match_percentage >= 50 ? 'secondary' : 'outline'}
                        className="text-xs h-5"
                      >
                        {t('dressCode.suggestions.match', { percent: group.match_percentage })}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs h-5">{t('dressCode.suggestions.generic')}</Badge>
                    )}
                    <span className="text-xs text-gray-400">
                      {t('dressCode.suggestions.cellsCount', { count: group.templates.length })}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1 text-gray-500 hover:text-gray-700"
                  onClick={() => setExpandedTemplate(isExpanded ? null : group.name)}
                >
                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  {isExpanded ? t('dressCode.suggestions.hide') : t('dressCode.suggestions.preview')}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="gap-1 bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => onApply(group.name)}
                >
                  {t('dressCode.suggestions.apply')}
                </Button>
              </div>
            </div>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="border-t border-gray-100 bg-gray-50 p-3">
                <p className="text-xs font-medium text-gray-700 mb-2">
                  {t('dressCode.suggestions.guidelinesCount', { count: group.templates.length })}
                </p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {group.templates.map(template => (
                    <div
                      key={`${template.gender}-${template.age_category}`}
                      className="bg-white rounded border border-gray-200 p-2 text-xs"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-[10px] h-4">
                          {GENDER_LABELS[template.gender]}
                        </Badge>
                        <Badge variant="secondary" className="text-[10px] h-4">
                          {AGE_CATEGORY_LABELS[template.age_category]}
                        </Badge>
                      </div>
                      <p className="text-gray-700 line-clamp-2">{template.description}</p>
                      {template.avoid && (
                        <p className="text-red-600 mt-1 line-clamp-1">
                          <span className="font-medium">{t('dressCode.suggestions.avoidLabel')}</span> {template.avoid}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TemplateSuggestions;

